import React from 'react';
import {observer} from 'mobx-react';
import SearchProductDiagram from './Diagram/SearchProductDiagram';
import SelectorTable from './Table/SelectorTable';
import SelectedTerms from './Legend/SelectedTerms';
import Dashboard from './Dashboard';
import {AnalyticsTab, Store, tKeyTab} from '../stores/store';
import {classes, stylesheet} from 'typestyle';
import {px} from 'csx';
import {mainContentClassName} from '../styles/style';
import {MemoryBarTab} from '../stores/MemoryBarStore';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {I18n, TProvider} from '../i18n/i18n';
import {CurrencyOptionMenu} from './Table/CurrencyOptionMenu';
import {SettingModal} from './SettingModal';
import ReportView from '../report/views/ReportView';
import {MemoryBarChooser} from './MemoryBar/ReportDefaultMemoryBar';
import {CiBlueColor, CiBlueGradientColor, GreyBlueBackgroundColor, WhiteColor, WarningColor, BlackColorFaded10, Shadow5} from '../styles/colors';
import {ExplorerParamsBar} from './ExplorerParamsBar/ExplorerParamsBar';

export const NAV_BAR_HEIGHT = 54;

@observer
export default class MainView extends React.Component<{store: Store}> {
    public generalClickOnApplication() {
        this.props.store.lineDiagramStore.toggleCustomDatepickerFoldout(true);
        this.props.store.selectorTableStore.closeOpenTableDropdowns();
    }

    public getMarginRight() {
        const {memoryBarStore, reportEditorStore} = this.props.store;
        return memoryBarStore.sidebarOpen || reportEditorStore.memoryBarIsOpenThroughReportView() ? 300 : 0;
    }

    public getMainView() {
        const {store} = this.props;
        switch (store.activeAnalyticsTab) {
            case AnalyticsTab.SearchView:
            case AnalyticsTab.CampaignView:
            case AnalyticsTab.ProductView:
                return <DiagramAndTable key={store.activeAnalyticsTab} store={store} />;
            case AnalyticsTab.Dashboard:
                return <Dashboard />;
            case AnalyticsTab.ReportView:
                return <ReportView />;
            default:
                throw new Error('Unknown active tab ' + store.activeAnalyticsTab);
        }
    }

    private openSettingModal = () => {
        this.props.store.settingModalOpen = true;
    };

    public render() {
        return (
            <div onClick={this.generalClickOnApplication.bind(this)} className={mainCss.Main}>
                <div className={mainCss.background} />
                <div className={mainCss.inner} style={{marginRight: this.getMarginRight()}}>
                    <div style={{position: 'absolute', top: 0, right: 0, height: NAV_BAR_HEIGHT, lineHeight: px(NAV_BAR_HEIGHT)}}>
                        {this.props.store.logoutButtonIsVisible ? <LogoutButton onClick={this.props.store.logoutUser} /> : null}
                        <SettingsButton onClick={this.openSettingModal} />
                        <MemoryBarOpener store={this.props.store} />
                    </div>
                    <AnalyticsTabs store={this.props.store} />
                    {this.getMainView()}
                </div>
                <MemoryBarChooser store={this.props.store} />
            </div>
        );
    }
}

@observer
class MemoryBarOpener extends React.Component<{store: Store}> {
    public openMemoryBar = (memoryTab: MemoryBarTab) => {
        this.props.store.memoryBarStore.changeActiveMemoryBarTab(memoryTab);
        this.props.store.memoryBarStore.toggleSidebar();
    };

    public getMemoryBarStyle() {
        if (this.props.store.memoryBarStore.historyItemAdded) {
            return {backgroundColor: WarningColor, color: WhiteColor};
        }
        return this.props.store.memoryBarStore.sidebarOpen ? {backgroundColor: 'transparent', color: WhiteColor} : {};
    }

    public render() {
        if (this.props.store.memoryBarStore.sidebarOpen) {
            return null;
        }

        return (
            <div className={mainCss.memoryBarOpener}>
                <div
                    className={classes(mainCss.memoryBarOpenerItem, 'additionalMargin')}
                    onClick={() => this.openMemoryBar(MemoryBarTab.HISTORY)}
                    style={this.getMemoryBarStyle()}>
                    <FontAwesomeIcon icon="history" transform="shrink-2" style={{marginRight: 5}} />
                    <I18n i18nKey="historyTabOpenerText" />
                </div>
                <div onClick={() => this.openMemoryBar(MemoryBarTab.FAVORITES)} className={mainCss.memoryBarOpenerItem}>
                    <FontAwesomeIcon icon="star" transform="shrink-2" style={{marginRight: 5}} />
                    <I18n i18nKey="favoriteTabOpenerText" />
                </div>
            </div>
        );
    }
}

const SettingsButton: React.FunctionComponent<{onClick: () => void}> = ({onClick}) => (
    <div className={mainCss.logoutButton} onClick={onClick}>
        <i className="fa fa-cog" aria-hidden="true" /> <I18n i18nKey="setting" />
    </div>
);

// tslint:disable-next-line:no-identical-functions
const LogoutButton: React.FunctionComponent<{onClick: () => void}> = ({onClick}) => (
    <div className={mainCss.logoutButton} onClick={onClick}>
        <i className="fa fa-sign-out" aria-hidden="true" /> <I18n i18nKey="logout" />
    </div>
);

const AnalyticsTabs: React.FunctionComponent<{store: Store}> = observer(({store}) => (
    <div className={tabCss.AnalyticsTabs}>
        <div className={tabCss.containerInner}>
            <AnalyticsTabComponent store={store} tabName={AnalyticsTab.Dashboard} />
            <AnalyticsTabComponent store={store} tabName={AnalyticsTab.SearchView} />
            <AnalyticsTabComponent store={store} tabName={AnalyticsTab.ProductView} />
            <AnalyticsTabComponent store={store} tabName={AnalyticsTab.CampaignView} />
            <AnalyticsTabComponent store={store} tabName={AnalyticsTab.ReportView} />
            <CurrencyOptionMenu store={store} />
        </div>
        {store.settingModalOpen ? <SettingModal store={store} /> : null}
    </div>
));

const AnalyticsTabComponent: React.FunctionComponent<{store: Store; tabName: AnalyticsTab}> = observer(({store, tabName}) => {
    const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        store.changeActiveAnalyticsTab(tabName);
    };
    let className = tabCss.tab;
    if (store.activeAnalyticsTab === tabName) {
        className = classes(className, tabCss.active);
    }

    if (!store.modalIsOpen) {
        className = classes(className, 'modalIsClosed');
    }

    return (
        <div className={className} onClick={onClick}>
            <a>
                <I18n i18nKey={tKeyTab(tabName)} />
            </a>
        </div>
    );
});

const DiagramAndTable: React.FunctionComponent<{store: Store}> = observer(({store}) => (
    <div className={mainContentClassName}>
        <ExplorerParamsBar store={store} />
        <SearchProductDiagram store={store} />
        <TProvider key={3}>{(t) => <SelectedTerms store={store} t={t} />}</TProvider>
        <SelectorTable store={store} />
    </div>
));

const mainCss = stylesheet({
    Main: {
        height: '100%',
    },
    background: {
        position: 'absolute',
        background: 'linear-gradient(to right,' + CiBlueColor + ' 0%, ' + CiBlueGradientColor + ' 100%)',
        left: '0',
        right: '0',
        paddingTop: '100px',
        display: 'none',
    },
    inner: {
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        height: '100%',
    },
    memoryBarOpenerItem: {
        WebkitTransition: '.2s',
        MozTransition: '.2s',
        OTransition: '.2s',
        transition: '.2s',
        display: 'inline-block',
        verticalAlign: 'top',
        cursor: 'pointer',
        paddingLeft: 15,
        paddingRight: 15,
        $nest: {
            '&.additionalMargin': {
                marginRight: 15,
            },
            '&:hover': {
                background: BlackColorFaded10,
            },
        },
    },
    memoryBarOpener: {
        display: 'inline-block',
        verticalAlign: 'top',
        paddingLeft: 16,
        paddingRight: 16,
        color: WhiteColor,
    },
    logoutButton: {
        display: 'inline-block',
        verticalAlign: 'top',
        cursor: 'pointer',
        paddingRight: 16,
        paddingLeft: 16,
        color: WhiteColor,
        $nest: {
            '&:hover': {
                background: BlackColorFaded10,
            },
        },
    },
});

const tabCss = stylesheet({
    AnalyticsTabs: {
        display: 'flex',
        alignItems: 'flex-end',
        height: NAV_BAR_HEIGHT,
        userSelect: 'none',
        background: 'linear-gradient(to right,' + CiBlueColor + ' 0%, ' + CiBlueGradientColor + ' 100%)',
    },
    containerInner: {
        paddingLeft: 20,
        whiteSpace: 'nowrap',
    },
    tabText: {
        height: 21,
    },
    tab: {
        cursor: 'pointer',
        borderTop: '1px solid ' + WhiteColor,
        display: 'inline-block',
        verticalAlign: 'top',
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: '.5rem',
        paddingBottom: '.5rem',
        borderBottom: '1px solid transparent',
        borderTopLeftRadius: 2,
        borderTopRightRadius: 2,
        backgroundColor: GreyBlueBackgroundColor,
        marginRight: 2,
        overflow: 'hidden',
        $nest: {
            '&.modalIsClosed': {
                transform: 'translateY(2px)',
                position: 'relative',
                $nest: {
                    '&::after': {
                        content: "''",
                        position: 'absolute',
                        bottom: '0',
                        left: -20,
                        right: -20,
                        top: -20,
                        boxShadow: Shadow5,
                    },
                },
            },
        },
    },
    active: {
        backgroundColor: WhiteColor,
        borderBottomColor: WhiteColor,
        borderTopColor: WhiteColor,
        $nest: {
            '&.modalIsClosed': {
                transform: 'translateY(0)',
                $nest: {
                    '&::after': {
                        boxShadow: 'none',
                    },
                },
            },
        },
    },
});



// WEBPACK FOOTER //
// ./src/views/MainView.tsx